import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@alchemer/ui/components/sonner';
import { useShareUsersMutation } from '@/api/mutations/useShareUsersMutation';
import { queryKeys } from '@/api/queryKeys';
import { canBeEditor, SHARE_PERMISSION, type ShareListItemData } from '@/types';
import { SharePermission, type ShareUserRequestSchema } from '@/api/generated/schemas';

export function useShareDialogSubmit(objectId: string | null) {
  const queryClient = useQueryClient();

  const mutation = useShareUsersMutation({
    onSuccess: (data) => {
      if (objectId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.shareUsers.byObjectId(objectId) });
      }
      if (data.warning) {
        toast.warning('Object shared with warnings', { description: data.warning });
      } else {
        toast.success('Object shared successfully');
      }
    },
    onError: () => {
      toast.error('Failed to save permissions', { description: 'Please try again.' });
    },
  });

  const submit = (
    sharedItems: ShareListItemData[],
    revokedUsers: ShareListItemData[],
    sendEmail: boolean,
    onSuccess: () => void,
  ) => {
    if (!objectId) {
      toast.error('Unable to save: content ID is missing. Please close and reopen the dialog.');
      return;
    }

    const revokedPayload: ShareUserRequestSchema[] = revokedUsers.map((item) => ({
      id: item.id,
      email: item.email,
      permission: SharePermission.NO_ACCESS,
      shareUnderlyingData: false,
    }));

    const activeUsers: ShareUserRequestSchema[] = sharedItems.map((item) => ({
      id: item.id,
      email: item.email,
      permission:
        item.sharedAsPermission === SHARE_PERMISSION.EDITOR && canBeEditor(item.userRole)
          ? SharePermission.MODIFY
          : SharePermission.READ_ONLY,
      shareUnderlyingData: item.shareUnderlyingData,
    }));

    mutation.mutate(
      {
        objectId,
        data: {
          objectId,
          sendEmail,
          users: [...activeUsers, ...revokedPayload],
        },
      },
      { onSuccess },
    );
  };

  return { submit, isPending: mutation.isPending };
}
